// MicroAnimations.js - Gestionnaire d'animations dynamiques
import { useEffect } from 'react';
import { gsap } from 'gsap';

const MicroAnimations = () => {
    useEffect(() => {
        // 1. EFFET DE PARTICULES FLOTTANTES
        const createFloatingParticles = () => {
            const particleCount = 5; // Limité pour les performances
            const container = document.body;

            for (let i = 0; i < particleCount; i++) {
                setTimeout(() => {
                    const particle = document.createElement('div');
                    particle.className = 'spark-particle';
                    particle.style.left = `${Math.random() * 100}%`;
                    particle.style.animationDelay = `${Math.random() * 10}s`;
                    particle.style.animationDuration = `${10 + Math.random() * 10}s`;
                    container.appendChild(particle);

                    // Nettoyer après l'animation
                    setTimeout(() => {
                        particle.remove();
                    }, 20000);
                }, i * 2000);
            }
        };

        // Créer des particules toutes les 20 secondes
        createFloatingParticles();
        const particleInterval = setInterval(createFloatingParticles, 20000);

        // 2. EFFET DE CURSEUR MAGNÉTIQUE SUR LES BOUTONS
        const magneticEffect = (e) => {
            const button = e.currentTarget;
            const rect = button.getBoundingClientRect();
            const x = e.clientX - rect.left - rect.width / 2;
            const y = e.clientY - rect.top - rect.height / 2;
            
            gsap.to(button, {
                x: x * 0.3,
                y: y * 0.3,
                duration: 0.3,
                ease: "power2.out"
            });
        };

        const resetMagnetic = (e) => {
            gsap.to(e.currentTarget, {
                x: 0,
                y: 0,
                duration: 0.3,
                ease: "elastic.out(1, 0.3)"
            });
        };

        // Appliquer l'effet magnétique à tous les boutons
        const buttons = document.querySelectorAll('.btnBase3, .btnBase4, button');
        buttons.forEach(button => {
            button.addEventListener('mousemove', magneticEffect);
            button.addEventListener('mouseleave', resetMagnetic);
        });

        // 3. EFFET DE TEXTE GLITCH AU HOVER
        const glitchEffect = (element) => {
            const originalText = element.textContent;
            const glitchChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
            let iterations = 0;
            
            const interval = setInterval(() => {
                element.textContent = originalText
                    .split('')
                    .map((char, index) => {
                        if (index < iterations) {
                            return originalText[index];
                        }
                        return glitchChars[Math.floor(Math.random() * glitchChars.length)];
                    })
                    .join('');
                
                if (iterations >= originalText.length) {
                    clearInterval(interval);
                }
                
                iterations += 1/3;
            }, 30);
        };

        // 4. ANIMATION DE NOMBRE COMPTE
        const animateValue = (element, start, end, duration) => {
            let startTimestamp = null;
            const step = (timestamp) => {
                if (!startTimestamp) startTimestamp = timestamp;
                const progress = Math.min((timestamp - startTimestamp) / duration, 1);
                element.textContent = Math.floor(progress * (end - start) + start);
                if (progress < 1) {
                    window.requestAnimationFrame(step);
                }
            };
            window.requestAnimationFrame(step);
        };

        // 5. EFFET DE RIPPLE AU CLIC
        const createRipple = (e) => {
            const button = e.currentTarget;
            const ripple = document.createElement('span');
            const rect = button.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);
            const x = e.clientX - rect.left - size / 2;
            const y = e.clientY - rect.top - size / 2;

            ripple.style.width = ripple.style.height = size + 'px';
            ripple.style.left = x + 'px';
            ripple.style.top = y + 'px';
            ripple.style.position = 'absolute';
            ripple.style.borderRadius = '50%';
            ripple.style.background = 'rgba(245, 152, 29, 0.5)';
            ripple.style.pointerEvents = 'none';
            ripple.style.animation = 'rippleEffect 0.6s ease-out';

            button.style.position = 'relative';
            button.style.overflow = 'hidden';
            button.appendChild(ripple);

            setTimeout(() => {
                ripple.remove();
            }, 600);
        };

        // Ajouter le ripple à tous les éléments cliquables
        const clickables = document.querySelectorAll('button, .btnBase3, .btnBase4, a');
        clickables.forEach(element => {
            element.addEventListener('click', createRipple);
        });

        // 6. OBSERVER POUR LES ANIMATIONS D'ENTRÉE
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const animateOnScroll = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.6s ease-out forwards';
                    animateOnScroll.unobserve(entry.target);
                }
            });
        }, observerOptions);

        // Observer les éléments pour l'animation d'entrée
        const animatedElements = document.querySelectorAll('h2, h3, p, .group');
        animatedElements.forEach(element => {
            if (!element.classList.contains('animated')) {
                element.style.opacity = '0';
                animateOnScroll.observe(element);
            }
        });

        // 7. EFFET DE CHALEUR SUR LE LOGO
        const logo = document.querySelector('img[alt="Logo"]');
        if (logo) {
            logo.addEventListener('mouseenter', () => {
                gsap.to(logo, {
                    filter: 'brightness(1.5) saturate(1.5) hue-rotate(10deg)',
                    scale: 1.05,
                    duration: 0.3
                });
            });

            logo.addEventListener('mouseleave', () => {
                gsap.to(logo, {
                    filter: 'brightness(1) saturate(1) hue-rotate(0deg)',
                    scale: 1,
                    duration: 0.3
                });
            });
        }

        // NETTOYAGE
        return () => {
            clearInterval(particleInterval);
            buttons.forEach(button => {
                button.removeEventListener('mousemove', magneticEffect);
                button.removeEventListener('mouseleave', resetMagnetic);
            });
            clickables.forEach(element => {
                element.removeEventListener('click', createRipple);
            });
            animateOnScroll.disconnect();
        };
    }, []);

    return null; // Ce composant ne rend rien visuellement
};

// CSS pour le ripple effect
const style = document.createElement('style');
style.textContent = `
    @keyframes rippleEffect {
        to {
            transform: scale(4);
            opacity: 0;
        }
    }

    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;
document.head.appendChild(style);

export default MicroAnimations;
